import React, { ReactElement } from 'react'
import { css, cx } from '@emotion/css'
import { GetItemPropsOptions } from 'downshift'
import { matchSorter } from 'match-sorter'
import { FormattedMessage, MessageDescriptor } from 'react-intl'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../styled'
import { classNames as textStyle } from '../text'
import {
  fnOrVal,
  comboboxMenuItemID,
  comboboxMenuID,
  ComboboxItem
} from './shared'
import { textBoxBorderStyle } from '../form-text-box'
import { getMatchData } from '../highlighting'
import { HighlightedText } from '../highlighting/highlighted-text'
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  WindowScroller
} from 'react-virtualized'

function Menu({ maxHeight, ...props }) {
  return (
    <div
      {...props}
      className={cx(
        'legacy-ds-combobox-menu',
        css`
          ${textBoxBorderStyle};
          box-shadow: 0 3px 4px -2px ${theme.color.neutral50};
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          border-top: none;
          width: 100%;
          background-color: ${theme.color.white};
          max-height: ${!!maxHeight ? maxHeight : '250px'};
          overflow-x: hidden;
          overflow-y: auto;
          position: absolute;
          left: 0;
          z-index: 1000;
          font-size: 14px;
          display: flex;
          flex-direction: column;
        `
      )}
    />
  )
}

interface MenuItemProps {
  readonly highlighted?: boolean
  readonly centered?: boolean
  readonly disabled?: boolean
  readonly children?: React.ReactNode
}

function MenuItem({
  highlighted,
  centered,
  disabled = false,
  ...props
}: MenuItemProps) {
  return (
    <div
      {...props}
      className={cx(
        'legacy-ds-combobox-menu-item',
        css`
          color: ${disabled && theme.color.neutral50};
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          padding: 7px 10px;
          transition: background-color 0.4s ease 0s;
          background-color: ${highlighted ? theme.color.teal10 : 'transparent'};
          display: flex;
          justify-content: ${centered ? 'center' : 'flex-start'};
          font-family: ${deprecatedTheme.fontFamilies.secondary};
        `
      )}
    />
  )
}

function GroupHeader(props) {
  return (
    <div
      {...props}
      className={css`
        ${textStyle.PanelTitle};
        white-space: nowrap;
        text-transform: uppercase;
        display: flex;
        flex-direction: column;
        padding: 4px 10px;
      `}
    />
  )
}

function Divider(props) {
  return (
    <div
      {...props}
      className={cx(
        'legacy-ds-dropdown-menu-item-divider',
        css`
          flex: 0 0;
          overflow: hidden;
          height: 1px;
          background-color: ${theme.color.neutral30};
          margin: 6px 0;
        `
      )}
    />
  )
}

const feedbackMessageCss = css`
  color: ${theme.color.neutral50};
`

interface Props<T> {
  readonly comboboxDataTestId?: string
  readonly values: T[]
  readonly items: Array<ComboboxItem<T>>
  readonly renderItemValue?: (item: T, inputValue: string) => React.ReactNode
  readonly itemIsDisabled?: (item: T) => boolean
  readonly itemToString?: (item: T) => string
  readonly itemToID?: (item: T) => string
  readonly itemsEqual?: (first: T, second: T) => boolean
  readonly getItemProps: (options: GetItemPropsOptions<T>) => any
  readonly highlightedIndex: number | null
  readonly inputValue: string
  readonly isAddAllowed?: boolean
  readonly createMessage?: MessageDescriptor
  readonly onNewItemClick?: (inputValue: string) => void
  readonly itemFromInput?: (inputValue: string) => T
  readonly noResultsText?: string
  readonly renderFooter?: () => React.ReactNode
  readonly tagExistsTranslationID?: string
  readonly maxHeight?: string
  readonly comboboxMenuItemToTestID?: (item: T) => string
}

interface State {
  readonly scrollElementRef: any
}

export class ComboBoxMenu<T> extends React.Component<Props<T>, State> {
  private itemsListRef: any
  private itemRowCache: CellMeasurerCache
  private scrollElementRef: any

  public constructor(props) {
    super(props)

    this.state = {
      scrollElementRef: undefined
    }

    this.itemRowCache = new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 50 // this is automatically replaced during rendering by CellMeasurer but this initial value is still required.
    })
  }

  public componentDidUpdate() {
    this.itemRowCache.clearAll() // since each menu item height is not fixed we need to re-calculate: cache & rowHeight
    if (this.itemsListRef) {
      this.itemsListRef.forceUpdateGrid() // this is necessary in order to make the menu items don't overlay each other for some scenarios (e.g. when searching by keyword) -> https://github.com/bvaughn/react-virtualized/issues/853#issuecomment-371084588
    }
  }

  public componentDidMount() {
    this.setState({ scrollElementRef: this.scrollElementRef })
  }

  public render() {
    const {
      values,
      items,
      renderItemValue,
      itemIsDisabled,
      itemToString,
      itemsEqual,
      itemToID,
      getItemProps,
      highlightedIndex,
      inputValue,
      isAddAllowed,
      createMessage,
      noResultsText,
      onNewItemClick,
      itemFromInput,
      comboboxDataTestId,
      renderFooter,
      tagExistsTranslationID = 'title_tagExists',
      maxHeight,
      comboboxMenuItemToTestID = () => comboboxMenuItemID
    } = this.props

    const { scrollElementRef } = this.state

    if (isAddAllowed) {
      if (!createMessage) {
        throw new Error(
          'createMessage prop is required if isAddAllowed is true'
        )
      }
      if (!onNewItemClick) {
        throw new Error(
          'onNewItemClick prop is required if isAddAllowed is true'
        )
      }
      if (!itemsEqual) {
        throw new Error('itemsEqual prop is required if isAddAllowed is true')
      }
      if (!itemFromInput) {
        throw new Error(
          'itemFromInput prop is required if isAddAllowed is true'
        )
      }
    }

    const visibleMenuItemData: Array<{
      readonly itemType:
        | 'groupDivider'
        | 'group'
        | 'item'
        | 'itemExists'
        | 'createNewItem'
      readonly item?: T
    }> = []
    const visibleMenuItems: Array<ReactElement<any>> = []
    let index = 0

    const itemsToShow = items.map(({ items: groupItems, name }) => {
      // filter out the current values from the items
      const filteredItems = groupItems.filter((i) =>
        values.every((v) => (itemsEqual ? !itemsEqual(v, i) : v !== i))
      )

      return {
        name,
        items: inputValue
          ? this.filterItemsByMatches<T>(
              filteredItems,
              inputValue,
              itemToString
            )
          : filteredItems
      }
    })

    for (const group of itemsToShow) {
      if (group.items.length < 1) {
        continue
      }

      if (group.name) {
        if (visibleMenuItems.length > 0) {
          visibleMenuItemData.push({
            itemType: 'groupDivider'
          })
          visibleMenuItems.push(<Divider key={`${group.name}-divider`} />)
        }

        visibleMenuItemData.push({
          itemType: 'group'
        })
        visibleMenuItems.push(
          <GroupHeader key={`${group.name}-header`} children={group.name} />
        )
      }

      for (const item of group.items) {
        const value = fnOrVal(item, itemToString, item)

        const fragments = getMatchData({
          match: inputValue,
          value
        })
        visibleMenuItemData.push({
          itemType: 'item',
          item
        })
        const disabled = item && itemIsDisabled ? itemIsDisabled(item) : false
        visibleMenuItems.push(
          <MenuItem
            data-testid={comboboxMenuItemToTestID(item)}
            key={fnOrVal(item, itemToID, item)}
            {...getItemProps({
              item,
              index,
              disabled
            })}
            highlighted={highlightedIndex === index}
            value={value}
          >
            {renderItemValue ? (
              renderItemValue(item, inputValue)
            ) : (
              <span>
                <HighlightedText fragments={fragments} />
              </span>
            )}
          </MenuItem>
        )
        index = index + 1
      }
    }

    if (isAddAllowed && inputValue.length > 0) {
      const existItem =
        values.filter((value) => {
          // TODO: This is 💩. It needed to be added after the fact in order
          // to get type checking to work because complex value types were
          // not considered when allowing user added values.
          if (typeof value !== 'string') {
            throw new Error(
              'isAllowed requires that autocomplete items and values be of type string'
            )
          }
          return value === inputValue
        }).length > 0

      const sameItem: boolean =
        visibleMenuItems.length === 1 &&
        visibleMenuItems[0].props.value === inputValue

      if (existItem) {
        visibleMenuItemData.push({
          itemType: 'itemExists'
        })
        visibleMenuItems.push(
          <MenuItem centered key={'menuItemTagExists'}>
            <span className={feedbackMessageCss}>
              <FormattedMessage id={tagExistsTranslationID} />
            </span>
          </MenuItem>
        )
      } else if (!sameItem) {
        const match =
          !!itemsEqual &&
          !!itemFromInput &&
          visibleMenuItemData.some((data) => {
            if (data.itemType === 'item') {
              return data.item
                ? itemsEqual(itemFromInput(inputValue), data.item)
                : false
            }
            return false
          })

        if (!match) {
          const item = inputValue
          visibleMenuItemData.push({
            itemType: 'createNewItem'
          })

          visibleMenuItems.push(
            <MenuItem
              data-testid={comboboxMenuItemID}
              data-e2e="combobox-menu-item"
              key={inputValue}
              {...getItemProps({
                item: fnOrVal(item, itemFromInput, item),
                index,
                onClick: () => {
                  if (onNewItemClick) {
                    onNewItemClick(item)
                  }
                }
              })}
              highlighted={highlightedIndex === index}
            >
              <span>
                {inputValue} (
                <FormattedMessage
                  {...(createMessage ? createMessage : { id: 'action_create' })}
                />
                )
              </span>
            </MenuItem>
          )
        }
      }
    }

    return (
      <Menu
        data-testid={comboboxDataTestId || comboboxMenuID}
        maxHeight={maxHeight}
      >
        {visibleMenuItems.length > 0 ? (
          <div
            ref={(ref) => {
              this.scrollElementRef = ref
            }}
            className={css`
              overflow: auto;
            `}
          >
            <WindowScroller scrollElement={scrollElementRef}>
              {({ height, scrollTop }) => (
                <div
                  className={css`
                    flex: 1;
                  `}
                >
                  <AutoSizer disableHeight>
                    {({ width }) => (
                      <List
                        ref={(ref) => (this.itemsListRef = ref)}
                        className={css`
                          border: 1px solid transparent;
                        `}
                        deferredMeasurementCache={this.itemRowCache}
                        autoHeight
                        scrollTop={scrollTop}
                        height={height}
                        width={width}
                        rowCount={visibleMenuItems.length}
                        rowHeight={this.itemRowCache.rowHeight}
                        rowRenderer={({
                          style,
                          index: rowIndex,
                          key,
                          parent
                        }) => {
                          return (
                            <CellMeasurer
                              cache={this.itemRowCache}
                              columnIndex={0}
                              key={key}
                              parent={parent}
                              rowIndex={rowIndex}
                            >
                              <div style={style}>
                                {visibleMenuItems[rowIndex]}
                              </div>
                            </CellMeasurer>
                          )
                        }}
                      />
                    )}
                  </AutoSizer>
                </div>
              )}
            </WindowScroller>
          </div>
        ) : (
          <MenuItem centered key={'menuItemNoResults'}>
            <span className={feedbackMessageCss}>
              {noResultsText ? (
                noResultsText
              ) : (
                <FormattedMessage id={'title_noResults'} />
              )}
            </span>
          </MenuItem>
        )}
        {renderFooter && renderFooter()}
      </Menu>
    )
  }

  private filterItemsByMatches<U>(
    filteredItems: U[],
    inputValue: string,
    itemToString?: (item: U) => string
  ): U[] {
    const itemsToMatchOn = filteredItems.map((item, originalIndex) => {
      return {
        matchOn: fnOrVal(item, itemToString, item),
        originalIndex
      }
    })

    const matches = matchSorter(
      // match on the string representation of the items if provided,
      // while preserving the original index of the item
      itemsToMatchOn,
      inputValue,
      {
        keys: ['matchOn']
      }
    )

    // map back to the original item via the index we saved
    return matches.map(({ originalIndex }) => filteredItems[originalIndex])
  }
}
